import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Check, Rocket, Phone, Zap, ShieldCheck, Clock, CreditCard, ClipboardList,
  Wand2, Eye, Globe, ChevronDown, Trash2, Sparkles,
  CalendarCheck, ImagePlus,
} from 'lucide-react';
import { OFFER } from '../constants/demos';
import { callDemo, demoErrorMessage } from '../components/demos/demoApi';

const STORAGE_KEY = 'gn_order_intake_v3';

const encode = (data) =>
  Object.keys(data)
    .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key] ?? ''))
    .join('&');

const TRADES = ['Cleaning', 'Handyman', 'Landscaping', 'Plumbing', 'Salon/Barber', 'Auto', 'Other'];

const DEFAULT_DATA = {
  // Step 1 — Build my free sample (the only required step)
  business_name: '',
  trade: '',
  city: '',
  phone: '',
  email: '',
  headache: '', // "biggest headache with your current site" — optional
  claim: '', // sample-site slug this order came from, e.g. ?claim=<slug>&biz=<name>

  // Optional "Add more" section
  existing_site: '',
  services_highlight: ['', '', ''], // top 3 services to highlight — no prices
  differentiator: '',
  top_questions: ['', '', ''],
  faq_answers: ['', '', ''], // parallel AI-drafted answers, sent along for value
  reviews: '',
  google_review_link: '',
  photo_urls: [''],
  about: '',
  book_pay_enabled: false,
  book_pay_link: '',
  copy_pref: 'refine', // 'refine' = let AI polish · 'exact' = keep their words verbatim
  style_notes: '',
};

function loadInitial() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_DATA;
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_DATA,
      ...parsed,
      services_highlight: parsed.services_highlight?.length === 3 ? parsed.services_highlight : DEFAULT_DATA.services_highlight,
      top_questions: parsed.top_questions?.length === 3 ? parsed.top_questions : DEFAULT_DATA.top_questions,
      faq_answers: parsed.faq_answers?.length === 3 ? parsed.faq_answers : DEFAULT_DATA.faq_answers,
      photo_urls: parsed.photo_urls?.length ? parsed.photo_urls : DEFAULT_DATA.photo_urls,
    };
  } catch {
    return DEFAULT_DATA;
  }
}

const inputCls = 'mt-1 bg-gray-900/80 border-gray-700 text-white placeholder:text-gray-500';

function buildSummary(d) {
  const lines = [];
  lines.push(`BUSINESS: ${d.business_name || '(not given)'}`);
  if (d.claim) lines.push(`CLAIMED FROM SAMPLE: ${d.claim}`);
  lines.push(`Trade/category: ${d.trade || '(not given)'}`);
  lines.push(`City: ${d.city || '(not given)'}`);
  lines.push(`Phone (click-to-call): ${d.phone || '(not given)'}`);
  lines.push(`Email: ${d.email || '(not given)'}`);
  lines.push(`Biggest headache with current site: ${d.headache || '(not given)'}`);
  lines.push('');
  lines.push(`Existing site/FB: ${d.existing_site || '(none)'}`);
  const services = d.services_highlight.filter(Boolean);
  lines.push(`Top services to highlight: ${services.length ? services.join(', ') : '(none given — AI will suggest)'}`);
  lines.push(`What makes them different: ${d.differentiator || '(none given — AI will suggest)'}`);
  const questions = d.top_questions.filter(Boolean);
  lines.push(`Top customer questions: ${questions.length ? questions.join(' | ') : '(none given — AI will suggest)'}`);
  lines.push(`Reviews pasted: ${d.reviews ? 'yes' : 'no'}`);
  lines.push(`Google review link: ${d.google_review_link || '(none)'}`);
  const photos = d.photo_urls.filter(Boolean);
  lines.push(`Photos: ${photos.length ? photos.join(', ') : `(none — may text to ${OFFER.phoneDisplay})`}`);
  lines.push(`Site intro/about: ${d.about || '(AI will draft from reviews or basics)'}`);
  lines.push(`Book Now / Pay Now button: ${d.book_pay_enabled ? 'YES — ' + (d.book_pay_link || '(link pending)') : 'no'}`);
  lines.push(`Copy preference: ${d.copy_pref === 'exact' ? 'use exact wording' : 'let AI polish'}`);
  if (d.style_notes) lines.push(`Style notes: ${d.style_notes}`);
  return lines.join('\n');
}

const AiButton = ({ onClick, loading, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    className="inline-flex items-center gap-2 text-sm font-semibold text-violet-300 bg-violet-500/10 border border-violet-500/30 rounded-lg px-3 py-2 hover:bg-violet-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
  >
    <Sparkles className="w-4 h-4" />
    {loading ? 'Thinking…' : children}
  </button>
);

const Order = () => {
  const [data, setData] = useState(loadInitial);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [addMoreOpen, setAddMoreOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // localStorage unavailable — progress just won't persist, form still works
    }
  }, [data]);

  // Prefill from a sample-site "Claim this site" link: ?claim=<slug>&biz=<business name>
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const claim = params.get('claim');
      const biz = params.get('biz');
      if (!claim && !biz) return;
      setData((d) => ({
        ...d,
        claim: claim || d.claim,
        business_name: d.business_name.trim() ? d.business_name : (biz || d.business_name),
      }));
    } catch {
      // no-op — malformed query string just skips prefill
    }
  }, []);

  const set = (field) => (e) => setData((d) => ({ ...d, [field]: e.target.value }));

  // ---------- highlighted services ----------
  const setServiceHighlight = (idx) => (e) =>
    setData((d) => {
      const services_highlight = [...d.services_highlight];
      services_highlight[idx] = e.target.value;
      return { ...d, services_highlight };
    });

  // ---------- top questions ----------
  const setTopQuestion = (idx) => (e) =>
    setData((d) => {
      const top_questions = [...d.top_questions];
      top_questions[idx] = e.target.value;
      return { ...d, top_questions };
    });

  // ---------- photos ----------
  const setPhoto = (idx) => (e) =>
    setData((d) => {
      const photo_urls = [...d.photo_urls];
      photo_urls[idx] = e.target.value;
      return { ...d, photo_urls };
    });
  const addPhoto = () =>
    setData((d) => (d.photo_urls.length >= 12 ? d : { ...d, photo_urls: [...d.photo_urls, ''] }));
  const removePhoto = (idx) =>
    setData((d) => ({ ...d, photo_urls: d.photo_urls.filter((_, i) => i !== idx) }));

  // ---------- AI assist: generate differentiator + intro from reviews ----------
  const [introAi, setIntroAi] = useState({ loading: false, error: '', result: null });
  const runIntroAi = async () => {
    if (!data.reviews.trim() || data.reviews.trim().length < 15) {
      setIntroAi({ loading: false, error: 'Paste at least one real review first (a sentence or two).', result: null });
      return;
    }
    setIntroAi({ loading: true, error: '', result: null });
    try {
      const res = await callDemo('review_intro', { reviews: data.reviews.trim(), context: 'intake' });
      setIntroAi({ loading: false, error: '', result: res });
    } catch (err) {
      setIntroAi({ loading: false, error: demoErrorMessage(err), result: null });
    }
  };
  const useIntro = () => {
    if (introAi.result?.intro) setData((d) => ({ ...d, about: introAi.result.intro }));
  };
  const useHeadlineAsDifferentiator = (headline) =>
    setData((d) => ({ ...d, differentiator: headline }));

  // ---------- AI assist: suggest top 3 services (no prices shown/stored) ----------
  const [servicesAi, setServicesAi] = useState({ loading: false, error: '', result: null });
  const runServicesAi = async () => {
    if (!data.trade.trim() || !data.city.trim()) {
      setServicesAi({ loading: false, error: 'Fill in your trade and city in Step 1 first.', result: null });
      return;
    }
    setServicesAi({ loading: true, error: '', result: null });
    try {
      const res = await callDemo('service_suggestions', {
        business_type: data.trade.trim(),
        city: data.city.trim(),
        context: 'intake',
      });
      setServicesAi({ loading: false, error: '', result: res });
    } catch (err) {
      setServicesAi({ loading: false, error: demoErrorMessage(err), result: null });
    }
  };
  const useSuggestedServices = () => {
    if (!servicesAi.result?.services?.length) return;
    const services_highlight = ['', '', ''];
    servicesAi.result.services.slice(0, 3).forEach((s, i) => { services_highlight[i] = s.name; });
    setData((d) => ({ ...d, services_highlight }));
  };

  // ---------- AI assist: suggest top customer questions ----------
  const [faqAi, setFaqAi] = useState({ loading: false, error: '', result: null });
  const runFaqAi = async () => {
    if (!data.trade.trim()) {
      setFaqAi({ loading: false, error: 'Fill in your trade in Step 1 first.', result: null });
      return;
    }
    setFaqAi({ loading: true, error: '', result: null });
    try {
      const res = await callDemo('faq_draft', {
        business_type: data.trade.trim(),
        city: data.city.trim(),
        context: 'intake',
      });
      setFaqAi({ loading: false, error: '', result: res });
    } catch (err) {
      setFaqAi({ loading: false, error: demoErrorMessage(err), result: null });
    }
  };
  const useSuggestedQuestions = () => {
    if (!faqAi.result?.faqs?.length) return;
    const top_questions = ['', '', ''];
    const faq_answers = ['', '', ''];
    faqAi.result.faqs.slice(0, 3).forEach((f, i) => { top_questions[i] = f.question; faq_answers[i] = f.answer; });
    setData((d) => ({ ...d, top_questions, faq_answers }));
  };

  // ---------- checklist summary ----------
  const checklist = useMemo(() => {
    const items = [
      'A 4–5 page professional website, mobile-ready',
      'AI answer bubble — answers your customers 24/7',
      'AI copy written from your real reviews',
      'Google Business Profile tune-up',
      'Click-to-call button',
      'Lead form that reaches you',
      "Installs on customers' phones like an app",
      'Google-review button + FAQ built for search engines',
      'Hours badge, service area & a QR code for flyers',
    ];
    if (data.book_pay_enabled) items.push('Book Now / Pay Now button');
    return items;
  }, [data.book_pay_enabled]);

  const canSubmit = data.business_name.trim() && data.trade.trim() && data.city.trim() && (data.phone.trim() || data.email.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('loading');
    const contact = data.phone && data.email ? `${data.phone} / ${data.email}` : data.phone || data.email;
    const payload = {
      business_name: data.business_name,
      claim: data.claim,
      industry: data.trade,
      current_website: data.existing_site,
      contact,
      style_notes: data.style_notes,
      about: data.about,
      copy_pref: data.copy_pref,
      intake_summary: buildSummary(data),
      intake_json: JSON.stringify(data),
    };
    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({ 'form-name': 'gn-order', ...payload }),
      });
      if (res.ok) {
        setStatus('success');
        try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const howItWorks = [
    { Icon: CreditCard, title: '1. Build your free sample', text: `Tell us the basics — pay $0. We build a real preview of your site before you decide anything.` },
    { Icon: ClipboardList, title: '2. Add more if you want (optional)', text: 'Reviews, photos, services — the more you give us, the better the sample. Saves as you go, nothing required.' },
    { Icon: Wand2, title: '3. Our AI team builds your sample', text: 'Design, copy, images generated for your business — then a human reviews it before you see it.' },
    { Icon: Eye, title: '4. You get a private preview', text: `You review the sample and decide. If you approve, it's $${OFFER.price} once — live on your own domain.` },
    { Icon: Globe, title: '5. You go live — within 48 hours', text: 'On your own domain (we connect it free) or a free subdomain. The site is yours — no monthly fees, ever.' },
  ];

  return (
    <div className="min-h-screen pt-16 pb-24 px-6">
      <div className="max-w-6xl mx-auto">

        {/* Header — 2026-09-18 conversion audit: at 390px-wide phones this block (badge +
            h1 + two paragraphs + tap-through phone line) ran ~500px tall before the form
            even started, pushing the "Build my free sample" submit button to ~1327px —
            well past the 844px fold (DOM-measured). Trimmed sizes/spacing on mobile only
            (sm: restores the original desktop look) and turned the phone line into a real
            44px+ tappable button so there's a legitimate in-fold call-to-action either way. */}
        <div className="text-center mb-6 sm:mb-10 pt-6 sm:pt-10">
          <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-full px-5 py-2 mb-4 sm:mb-6">
            <Zap className="w-4 h-4 text-green-400" />
            <span className="text-green-300 text-sm font-semibold">
              Founding rate — first {OFFER.foundingSpots} sites at ${OFFER.price}, then ${OFFER.anchorPrice}
            </span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-white mb-3 sm:mb-4">Build My Free Sample</h1>
          <p className="text-base sm:text-xl text-gray-400 max-w-2xl mx-auto">
            Pay $0 until you approve it. See your real site before you decide — then ${OFFER.price} once, live in 48 hours, no monthly fees.
          </p>
          <p className="text-gray-400 mt-4">
            Prefer to talk it through first?
          </p>
          <a
            href={`tel:${OFFER.phoneTel}`}
            className="mt-2 inline-flex items-center justify-center gap-2 min-h-[44px] px-5 py-2.5 rounded-xl border border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 hover:text-blue-200 font-semibold transition-colors"
          >
            <Phone className="w-4 h-4" />
            {OFFER.phoneDisplay} <span className="text-blue-400/80 font-normal">— answered 24/7</span>
          </a>
          <p className="text-gray-500 text-xs mt-3">Your answers save automatically on this device — close this tab and pick up later.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10 mb-16 items-start">

          <form onSubmit={handleSubmit} className="space-y-5">

            {status === 'success' ? (
              <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-8 text-center">
                <div className="text-4xl mb-3">🎉</div>
                <h3 className="text-xl font-bold text-white mb-2">Got it — building your free sample!</h3>
                <p className="text-green-300 mb-6">
                  We'll have a private preview ready for you shortly — no charge, no obligation.
                  When you approve it, secure your founding spot below and your 48-hour clock starts.
                </p>
                <a href={OFFER.payhipUrl} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8 py-5 text-lg rounded-xl shadow-lg shadow-green-500/30 w-full">
                    <Rocket className="w-5 h-5 mr-2" />
                    Approved — pay ${OFFER.price} now
                  </Button>
                </a>
              </div>
            ) : (
              <>
                {/* Step 1 — the only required step */}
                <Card className="bg-gray-800/50 border-gray-700 p-6 sm:p-8">
                  <h2 className="text-2xl font-bold text-white mb-1">Build my free sample</h2>
                  <p className="text-gray-400 text-sm mb-6">The only required step — under 2 minutes from your phone. Pay $0 until you approve your sample.</p>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="business_name" className="text-gray-300">Business name *</Label>
                      <Input id="business_name" value={data.business_name} onChange={set('business_name')} required placeholder="e.g., Sunrise Cleaning Co." className={inputCls} />
                    </div>
                    <div>
                      <Label htmlFor="trade" className="text-gray-300">Trade *</Label>
                      <select
                        id="trade"
                        value={data.trade}
                        onChange={set('trade')}
                        required
                        className={inputCls + ' w-full rounded-md border h-10 px-3 text-sm'}
                      >
                        <option value="" disabled>Choose one…</option>
                        {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="city" className="text-gray-300">City *</Label>
                      <Input id="city" value={data.city} onChange={set('city')} required placeholder="e.g., Orlando, FL" className={inputCls} />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-gray-300">Phone <span className="text-gray-500">(click-to-call)</span></Label>
                      <Input id="phone" type="tel" value={data.phone} onChange={set('phone')} placeholder="(555) 555-5555" className={inputCls} />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="email" className="text-gray-300">Email</Label>
                      <Input id="email" type="email" value={data.email} onChange={set('email')} placeholder="you@business.com" className={inputCls} />
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="headache" className="text-gray-300">Biggest headache with your current site? <span className="text-gray-500">(optional)</span></Label>
                    <Input id="headache" value={data.headache} onChange={set('headache')} placeholder="e.g., looks outdated, nobody calls from it, don't have one at all…" className={inputCls} />
                  </div>

                  {!canSubmit && (
                    <p className="text-amber-400 text-xs mt-4">Business name, trade, city, and a phone or email are all it takes.</p>
                  )}

                  <Button
                    type="submit"
                    disabled={status === 'loading' || !canSubmit}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-5 text-lg rounded-xl mt-5"
                  >
                    {status === 'loading' ? 'Sending…' : 'Build my free sample — pay $0 until you approve it'}
                  </Button>

                  {status === 'error' && (
                    <p className="text-red-400 text-sm mt-3">
                      Something went wrong — email us at support@globalnow.biz or call{' '}
                      <a href={`tel:${OFFER.phoneTel}`} className="underline">{OFFER.phoneDisplay}</a>.
                    </p>
                  )}
                </Card>

                {/* Optional "Add more" section */}
                <details
                  open={addMoreOpen}
                  onToggle={(e) => setAddMoreOpen(e.target.open)}
                  className="group bg-gray-800/50 border border-gray-700 rounded-2xl overflow-hidden"
                >
                  <summary className="list-none cursor-pointer select-none flex items-center justify-between gap-4 p-6 hover:bg-gray-800/80 transition-colors [&::-webkit-details-marker]:hidden">
                    <div>
                      <h3 className="text-lg font-bold text-white">Want it even better? Add more</h3>
                      <p className="text-gray-400 text-sm">Optional, saves as you go — nothing here is required.</p>
                    </div>
                    <ChevronDown className="w-5 h-5 text-gray-400 shrink-0 transition-transform group-open:rotate-180" />
                  </summary>

                  <div className="px-6 pb-6 space-y-6 border-t border-gray-700 pt-5">

                    <div>
                      <Label htmlFor="existing_site" className="text-gray-300">Current website or Facebook page <span className="text-gray-500">(optional)</span></Label>
                      <Input id="existing_site" value={data.existing_site} onChange={set('existing_site')} placeholder="No website yet / a Facebook page / oldsite.com…" className={inputCls} />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <Label className="text-gray-300">Top 3 services to highlight</Label>
                        <AiButton onClick={runServicesAi} loading={servicesAi.loading}>Suggest my top 3 services</AiButton>
                      </div>
                      {servicesAi.error && <p className="text-red-400 text-xs mb-2">{servicesAi.error}</p>}
                      {servicesAi.result && (
                        <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3 mb-3 space-y-1">
                          <p className="text-xs text-violet-300">{servicesAi.result.note || 'Suggestions only — edit anything before it goes on your site.'}</p>
                          {servicesAi.result.services.map((s, i) => (
                            <p key={i} className="text-sm text-gray-200">{s.name} <span className="text-gray-500 text-xs">— typically {s.price_range}</span></p>
                          ))}
                          <button type="button" onClick={useSuggestedServices} className="text-xs font-semibold text-blue-400 hover:text-blue-300">Use these →</button>
                        </div>
                      )}
                      <div className="space-y-2">
                        {data.services_highlight.map((s, idx) => (
                          <Input key={idx} value={s} onChange={setServiceHighlight(idx)} placeholder={`Service ${idx + 1}`} className={inputCls} />
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="differentiator" className="text-gray-300">What makes you different?</Label>
                      <Textarea id="differentiator" value={data.differentiator} onChange={set('differentiator')} rows={2} placeholder="Licensed & insured, family-owned since 2010, same-day service… or generate one from your reviews below." className={inputCls} />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <Label className="text-gray-300">Top 3 things customers ask</Label>
                        <AiButton onClick={runFaqAi} loading={faqAi.loading}>Suggest my top customer questions</AiButton>
                      </div>
                      {faqAi.error && <p className="text-red-400 text-xs mb-2">{faqAi.error}</p>}
                      {faqAi.result && (
                        <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3 mb-3 space-y-2">
                          {faqAi.result.faqs.map((f, i) => (
                            <div key={i} className="text-sm">
                              <p className="text-white font-semibold">{f.question}</p>
                              <p className="text-gray-400 text-xs">{f.answer}</p>
                            </div>
                          ))}
                          <button type="button" onClick={useSuggestedQuestions} className="text-xs font-semibold text-blue-400 hover:text-blue-300">Use these →</button>
                        </div>
                      )}
                      <div className="space-y-2">
                        {data.top_questions.map((q, idx) => (
                          <Input key={idx} value={q} onChange={setTopQuestion(idx)} placeholder={`Question ${idx + 1}`} className={inputCls} />
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <Label htmlFor="reviews" className="text-gray-300">Paste 2–3 real reviews</Label>
                        <AiButton onClick={runIntroAi} loading={introAi.loading}>Write my intro from these reviews</AiButton>
                      </div>
                      <Textarea id="reviews" value={data.reviews} onChange={set('reviews')} rows={4} placeholder="Paste a couple of real customer reviews here…" className={inputCls} />
                      {introAi.error && <p className="text-red-400 text-xs mt-2">{introAi.error}</p>}
                      {introAi.result && (
                        <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3 mt-2 space-y-2">
                          <p className="text-sm text-gray-200">&ldquo;{introAi.result.intro}&rdquo;</p>
                          {introAi.result.headlines?.length > 0 && (
                            <div className="space-y-1">
                              {introAi.result.headlines.map((h, i) => (
                                <div key={i} className="flex items-center justify-between gap-2 text-xs bg-gray-900/60 rounded px-2 py-1.5">
                                  <span className="text-gray-300">{h}</span>
                                  <button type="button" onClick={() => useHeadlineAsDifferentiator(h)} className="text-blue-400 hover:text-blue-300 font-semibold shrink-0">Use as differentiator →</button>
                                </div>
                              ))}
                            </div>
                          )}
                          <button type="button" onClick={useIntro} className="text-xs font-semibold text-blue-400 hover:text-blue-300">Use this as my site intro →</button>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="about" className="text-gray-300">Site intro / about your business <span className="text-gray-500">(optional)</span></Label>
                      <Textarea id="about" value={data.about} onChange={set('about')} rows={3} placeholder="Who you are, what you offer — or use the AI button above." className={inputCls} />
                    </div>

                    <div>
                      <Label htmlFor="google_review_link" className="text-gray-300">Google review link <span className="text-gray-500">(instead of pasting reviews)</span></Label>
                      <Input id="google_review_link" value={data.google_review_link} onChange={set('google_review_link')} placeholder="https://g.page/r/…" className={inputCls} />
                    </div>

                    <div>
                      <Label className="text-gray-300 mb-2 block">Photos <span className="text-gray-500">(up to 12 — paste image links, or text them to {OFFER.phoneDisplay})</span></Label>
                      <div className="space-y-2">
                        {data.photo_urls.map((url, idx) => (
                          <div key={idx} className="flex gap-2">
                            <Input value={url} onChange={setPhoto(idx)} placeholder="https://… (a photo link)" className={inputCls + ' flex-1'} />
                            {data.photo_urls.length > 1 && (
                              <button type="button" onClick={() => removePhoto(idx)} className="text-gray-500 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
                            )}
                          </div>
                        ))}
                      </div>
                      {data.photo_urls.length < 12 && (
                        <button type="button" onClick={addPhoto} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-400 hover:text-blue-300">
                          <ImagePlus className="w-3.5 h-3.5" /> Add another photo link
                        </button>
                      )}
                    </div>

                    <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" checked={data.book_pay_enabled} onChange={() => setData((d) => ({ ...d, book_pay_enabled: !d.book_pay_enabled }))} className="mt-1 accent-blue-500" />
                        <span>
                          <span className="flex items-center gap-1.5 text-sm font-semibold text-white"><CalendarCheck className="w-4 h-4 text-blue-300" /> Add a Book Now / Pay Now button <span className="text-gray-500 font-normal">(we set it up for you)</span></span>
                          <span className="text-gray-400 text-xs">Link your Calendly, Square, Google booking, PayPal.me, Venmo, or Stripe page. We don't process payments — this just links out.</span>
                        </span>
                      </label>
                      {data.book_pay_enabled && (
                        <Input value={data.book_pay_link} onChange={set('book_pay_link')} placeholder="Paste your booking or payment link" className={inputCls + ' mt-2 ml-7'} />
                      )}
                    </div>

                    <div>
                      <Label className="text-gray-300">Your words — how should we handle them?</Label>
                      <div className="mt-2 space-y-2">
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 bg-gray-900/60 cursor-pointer hover:border-blue-500 transition-colors">
                          <input type="radio" name="copy_pref" value="refine" checked={data.copy_pref === 'refine'} onChange={set('copy_pref')} className="mt-1 accent-blue-500" />
                          <span className="text-sm text-gray-200"><span className="font-semibold text-white">✨ Polish it for me</span> <span className="text-xs text-green-400">(recommended)</span><br /><span className="text-gray-400">Our AI turns what you gave us into clear, professional website copy.</span></span>
                        </label>
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 bg-gray-900/60 cursor-pointer hover:border-blue-500 transition-colors">
                          <input type="radio" name="copy_pref" value="exact" checked={data.copy_pref === 'exact'} onChange={set('copy_pref')} className="mt-1 accent-blue-500" />
                          <span className="text-sm text-gray-200"><span className="font-semibold text-white">📝 Use my exact wording</span><br /><span className="text-gray-400">Keep my text word-for-word — we'll only fix obvious typos.</span></span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="style_notes" className="text-gray-300">Style notes — anything you love or hate? <span className="text-gray-500">(optional)</span></Label>
                      <Textarea id="style_notes" value={data.style_notes} onChange={set('style_notes')} rows={3} placeholder="Colors, sites you like, must-have pages, the vibe you want…" className={inputCls} />
                    </div>

                    <Button
                      type="submit"
                      disabled={status === 'loading' || !canSubmit}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-5 text-lg rounded-xl"
                    >
                      {status === 'loading' ? 'Sending…' : 'Build my free sample — pay $0 until you approve it'}
                    </Button>
                  </div>
                </details>
              </>
            )}
          </form>

          {/* Sidebar: summary + payment + reassurance */}
          <div className="space-y-6 lg:sticky lg:top-24">
            <Card className="bg-gray-800/50 border-gray-700 p-6">
              <h2 className="text-lg font-bold text-white mb-1">Your $199 site includes</h2>
              <p className="text-gray-500 text-xs mb-4">Updates live as you fill things in.</p>
              <div className="space-y-2">
                {checklist.map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="mt-0.5 bg-green-500/20 rounded-full p-0.5 shrink-0">
                      <Check className="w-3 h-3 text-green-400" />
                    </div>
                    <span className="text-gray-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 pt-4 border-t border-gray-700">The sample is free. $199 only happens once you approve it — no monthly fees, ever.</p>
              <p className="text-gray-500 text-xs mt-2">Bells &amp; whistles — what agencies charge extra for, included free: Google-review button, FAQ answers built for search engines, hours with an open-now badge, your service area listed, a "save our number" contact card, and a QR code for flyers.</p>
            </Card>

            <Card className="bg-gray-800/50 border-gray-700 p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-5 py-1.5 rounded-bl-2xl text-sm font-bold flex items-center gap-1">
                <Zap className="w-3 h-3" />
                FOUNDING RATE
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">After you approve your sample</h2>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-5xl font-bold text-white">${OFFER.price}</span>
                <span className="text-gray-400">one-time</span>
              </div>
              <p className="text-gray-500 mb-6">
                <span className="line-through">${OFFER.anchorPrice}</span>{' '}
                <span className="text-green-400">after the first {OFFER.foundingSpots} orders</span>
              </p>
              <a href={OFFER.payhipUrl} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-green-500/30 w-full">
                  <Rocket className="w-5 h-5 mr-2" />
                  Pay ${OFFER.price} — Secure Checkout
                </Button>
              </a>
              <p className="text-gray-500 text-xs mt-4 text-center">
                Secure checkout via Payhip • cards, PayPal & more • instant receipt
              </p>
              <div className="mt-6 pt-6 border-t border-gray-700 space-y-3">
                {[
                  'Free sample — pay $0 until you approve it',
                  'Live within 48 hours of approval',
                  'One full revision round included',
                  '30-day satisfaction guarantee',
                  'No monthly fees — the site is yours',
                ].map((line, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="mt-0.5 bg-green-500/20 rounded-full p-1">
                      <Check className="w-3.5 h-3.5 text-green-400" />
                    </div>
                    <span className="text-gray-300 text-sm">{line}</span>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="bg-blue-500/5 border-blue-500/20 p-6">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-6 h-6 text-blue-400 shrink-0 mt-0.5" />
                <p className="text-gray-300 text-sm leading-relaxed">
                  <span className="text-white font-semibold">No payment required to get your sample.</span>{' '}
                  We only ask for $199 after you've seen and approved your real site.
                  Not sure yet?{' '}
                  <Link to="/examples" className="text-blue-400 hover:underline">See live examples</Link>{' '}
                  or call <a href={`tel:${OFFER.phoneTel}`} className="text-blue-400 hover:underline">{OFFER.phoneDisplay}</a>.
                </p>
              </div>
            </Card>
          </div>
        </div>

        {/* What happens next */}
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-block p-3 bg-green-500/10 rounded-full mb-4">
              <Clock className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-3xl font-bold text-white">What happens next</h2>
          </div>
          <div className="space-y-4">
            {howItWorks.map((step, idx) => (
              <Card key={idx} className="bg-gray-800/50 border-gray-700 p-5 flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center shrink-0">
                  <step.Icon className="w-5 h-5 text-blue-300" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">{step.title}</h3>
                  <p className="text-gray-400 text-sm">{step.text}</p>
                </div>
              </Card>
            ))}
          </div>
          <p className="text-center text-gray-500 text-sm mt-8">
            Questions at any step? Call{' '}
            <a href={`tel:${OFFER.phoneTel}`} className="text-blue-400 hover:underline">{OFFER.phoneDisplay}</a>{' '}
            (24/7) or email support@globalnow.biz.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Order;
